import { useCallback, useEffect, useRef, useState } from 'react'
import BackRow from './BackButton'
import Icon from '../components/Icon'
import { imageUrl, worx, type Category, type JobCard, type JobDetail, type MatchResult } from './client'
import Report from './Report'
import DemoNotice from './DemoNotice'
import { useTranslation } from './Translated'
import { colorIdFor, serviceColorHex } from '../../shared/serviceColors'
import { categoryName, categoryShort, greeting, payLabel, payShort, tw, type WorxKey } from './strings'
import { celebrate } from './feel'

/* ── The card ─────────────────────────────────────────────────────────────── */

/**
 * One posting in a list.
 *
 * The same component in the feed, in Saved and in the employer's own listing,
 * so a job looks like itself everywhere. Status is shown only when it is not
 * `published`: a badge saying "published" on every row in a feed of published
 * jobs is noise, while "Filled" on a saved one is the whole message.
 */
export function Card({
  job,
  onOpen,
  highlight = false,
  matchReasons,
  saved,
  onToggleSave,
  children,
}: {
  job: JobCard
  onOpen?: () => void
  /** The posting the server picked out — see services/api/src/worx/matching.ts. */
  highlight?: boolean
  /** What it matched on, so the badge can say rather than just assert. */
  matchReasons?: ('category' | 'arrangement' | 'pay')[]
  saved?: boolean
  onToggleSave?: () => void
  children?: React.ReactNode
}) {
  const ended = job.status === 'filled' || job.status === 'closed'
  const classes = ['w-card', 'w-svc', ended && 'w-card-ended', highlight && 'w-card-match'].filter(Boolean).join(' ')
  const artUrl = imageUrl(job.employerLogoId)

  /*
   * ── One row of facts, not three ──────────────────────────────────────────
   *
   * The card used to give pay, hours and place a round icon each and a row
   * each, plus a footer with a tagline and a View Job button — five rows per
   * posting, so two cards filled a phone. A feed is a list somebody scans,
   * and the whole card is the tap target, so the button was a second way to
   * do what tapping the card already did.
   *
   * Place and hours share a line with a separator; pay is the chip below,
   * because pay is the one thing people scan FOR.
   */
  return (
    <article className={classes} style={{ '--svc-color': serviceColorHex(colorIdFor(job.employerId)) } as React.CSSProperties}>
      <button className="w-card-main" onClick={onOpen} disabled={!onOpen}>
        <span className="w-card-body">
          {highlight && (
            <span className="w-match" title={reasonText(matchReasons)}>
              <Icon name="star" size={12} /> {tw('w.feed.match')}
            </span>
          )}

          <span className="w-card-title">{job.titleTranslated ?? job.title}</span>
          <span className="w-card-employer">{job.employerName}</span>

          <span className="w-card-where">
            {job.location && <><Icon name="home" size={14} /> {job.location}</>}
            {job.location && job.arrangement && <span className="w-dot" aria-hidden="true">•</span>}
            {job.arrangement && tw(`w.arr.${job.arrangement}` as never)}
          </span>

          <span className={job.payMin === null && job.payMax === null ? 'w-pay-chip muted' : 'w-pay-chip'}>
            {payLabel(job)}
          </span>

          <span className="w-posted">{posted(job.publishedAt)}</span>

          {job.isDemo && <span className="w-pill">{tw('w.demo.badge')}</span>}
          {job.status !== 'published' && (
            <span className={`w-pill w-pill-${job.status}`}>{tw(`w.jobstatus.${job.status}` as WorxKey) ?? job.status}</span>
          )}
          {typeof job.applicants === 'number' && (
            <span className="w-card-applicants">
              {job.applicants} {tw(job.applicants === 1 ? 'w.ap.applicant' : 'w.ap.applicants')}
              {Boolean(job.unreviewed) && <span className="w-new"> · {job.unreviewed} {tw('w.applicants.new')}</span>}
            </span>
          )}
        </span>

        {/* The business's block: its colour, and its logo or its initials. Same
            shape as an ad's, flush to the card's top, right and bottom edges. */}
        <span className="w-svc-art" aria-hidden="true">
          {artUrl
            ? <img src={artUrl} alt="" loading="lazy" decoding="async" />
            : <span className="w-svc-initials">{initials(job.employerName)}</span>}
        </span>

        {onToggleSave && (
          <span
            className="w-bookmark"
            role="button"
            tabIndex={0}
            aria-pressed={Boolean(saved)}
            aria-label={saved ? tw('w.jobs.unsave') : tw('w.jobs.save')}
            onClick={(e) => { e.stopPropagation(); onToggleSave() }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return
              e.preventDefault()
              e.stopPropagation()
              onToggleSave()
            }}
          >
            <Icon name="bookmark" size={20} className={saved ? 'w-icon-filled' : undefined} />
          </span>
        )}
      </button>
      {children}
    </article>
  )
}

/** "Posted 2 days ago", in the reader's language. */
function posted(at: number | null): string {
  if (!at) return ''
  const hours = Math.round((at - Date.now()) / 3_600_000)
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  // Hours for anything today — "Posted 2h ago" is what the design shows, and
  // "posted today" is vaguer than the app needs to be about a fresh posting.
  const when = hours > -24 ? rtf.format(hours, 'hour') : rtf.format(Math.round(hours / 24), 'day')
  return `${tw('w.feed.posted')} ${when}`
}

/**
 * What the badge matched on, in words.
 *
 * On the title rather than in the badge: the badge has room for three words on
 * a phone, and the reasons are the answer to "why this one" rather than part
 * of the claim itself.
 */
function reasonText(reasons?: ('category' | 'arrangement' | 'pay')[]): string | undefined {
  if (!reasons?.length) return undefined
  return reasons.map((r) => tw(`w.feed.why.${r}` as never)).join(' · ')
}

/** A business's logo, or its initials in the same tile. */
export function Logo({ name, src, className = 'w-logo' }: { name: string; src?: string | null; className?: string }) {
  const url = imageUrl(src)
  if (url) return <img className={className} src={url} alt="" width={52} height={52} loading="lazy" decoding="async" />
  return <span className={className} aria-hidden="true">{initials(name)}</span>
}

/**
 * Up to two initials from a business name.
 *
 * Words like "the" and "la" are skipped so "La Palma Café" reads LP rather
 * than LA — the article is the least distinguishing part of a name and two
 * businesses on this island start with one.
 */
export function initials(name: string): string {
  const SKIP = new Set(['the', 'la', 'el', 'los', 'las', 'de', 'del', 'and', 'y', '&'])
  const words = name.split(/\s+/).filter((w) => w && !SKIP.has(w.toLowerCase()))
  const source = words.length ? words : name.split(/\s+/)
  return source.slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')
}

/* ── The feed ─────────────────────────────────────────────────────────────── */

export function Feed({
  onOpen,
  greetingName,
  onNeedProfile,
}: {
  onOpen: (id: string) => void
  /** Present only when somebody is signed in — see the header below. */
  greetingName?: string
  /** Takes them to the Profile tab from the nudge. */
  onNeedProfile?: () => void
}) {
  const [jobs, setJobs] = useState<JobCard[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [q, setQ] = useState('')
  const [category, setCategory] = useState('')
  const [next, setNext] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [demoAccount, setDemoAccount] = useState(false)
  const [saved, setSaved] = useState<Set<string>>(new Set())
  /*
   * The match, as decided by the server.
   *
   * The client does not work this out any more — it renders what the feed
   * endpoint returns, including the REASONS, so the badge can say what it
   * matched on. The rule itself is worx/matching.ts.
   */
  const [match, setMatch] = useState<MatchResult | null>(null)
  const [matchable, setMatchable] = useState(true)
  const [sheet, setSheet] = useState(false)

  const load = useCallback(async (opts: { append?: boolean; before?: number } = {}) => {
    setLoading(true)
    setLoadError(false)
    try {
      const r = await worx.feed({ q, category, before: opts.before })
      setJobs((prev) => (opts.append ? [...prev, ...r.jobs] : r.jobs))
      setNext(r.next)
      setDemoAccount(r.demoAccount)
      // Only the first page carries one; appending must not clear it.
      if (!opts.append) setMatch(r.match)
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [q, category])

  useEffect(() => {
    void worx.categories().then((c) => setCategories(c.categories)).catch(() => {})
    // Signed out, there is no profile and nothing is highlighted.
    if (!greetingName) return
    void worx.savedJobs().then((r) => setSaved(new Set(r.jobs.map((j) => j.id)))).catch(() => {})
    // Only to decide whether to show the nudge below — the match itself is
    // the server's call.
    void worx.profile().then((r) => setMatchable(Boolean(r.profile?.categories.length))).catch(() => {})
  }, [greetingName])

  /*
   * Debounced, because this fires on every keystroke. 250ms is short enough to
   * feel immediate and long enough that typing "bartender" is one query rather
   * than nine.
   */
  useEffect(() => {
    const timer = setTimeout(() => void load(), 250)
    return () => clearTimeout(timer)
  }, [load])

  /*
   * TWO named chips, plus All, plus More — four controls on one line.
   *
   * Five wrapped onto a second row at 390px, and a "More" button sitting alone
   * under four others reads as an afterthought rather than the way to the
   * rest. Four fits, so four it is, and everything else is one tap away in the
   * sheet.
   *
   * `PROMINENT` is a fixed list rather than a usage ranking: a row that
   * reorders itself under people is one nobody can learn. Appending the
   * current selection means choosing "Marine" from the sheet leaves a visible,
   * pressed "Marine" chip rather than a row that looks unfiltered.
   */
  const PROMINENT = ['hospitality', 'construction']
  /*
   * Published since midnight where the business is — not "in the last 24
   * hours". "Today" on an island means today there, and a worker opening the
   * app at 8am wants what went up this morning.
   */
  const startOfDay = (() => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Puerto_Rico', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date())
    return new Date(`${parts}T00:00:00-04:00`).getTime()
  })()
  const newToday = jobs.filter((j) => (j.publishedAt ?? 0) >= startOfDay).length

  const shown = categories.filter(
    (c) => PROMINENT.includes(c.id) || c.id === category,
  )

  async function toggleSave(id: string) {
    // Optimistic: a bookmark that waits for a round trip before it fills in
    // feels broken. A failure puts it back.
    const wanted = !saved.has(id)
    setSaved((prev) => {
      const next = new Set(prev)
      if (wanted) next.add(id)
      else next.delete(id)
      return next
    })
    try {
      await worx.save(id, wanted)
    } catch {
      setSaved((prev) => {
        const next = new Set(prev)
        if (wanted) next.delete(id)
        else next.add(id)
        return next
      })
    }
  }

  return (
    <div className="w-feed">
      {/*
        The greeting IS the heading on a signed-in home screen.

        "Find work on Vieques" is the right title for a visitor who has just
        arrived and does not know what this is. For somebody who opens the app
        every morning it is a banner restating the obvious, so their name takes
        the space and the purpose drops to a sub-line.
      */}
      <h1 className="w-feed-heading">
        {greetingName ? `${greeting()}, ${greetingName}` : tw('w.feed.heading')}
      </h1>
      {greetingName && <p className="w-feed-sub">{tw('w.feed.subtitle')}</p>}

      {demoAccount && <DemoNotice practice />}
      <div className="w-search">
        <Icon name="search" size={20} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={tw('w.feed.search')}
          aria-label={tw('w.feed.search')}
        />
      </div>

      {/*
        ── Four chips and a More button, not fourteen chips ──────────────────

        A scrolling row of all fourteen was the first attempt, and at 375px it
        is a row nobody reaches the end of: "Hospitality & Lodging" and
        "Construction & Trades" are each most of the screen, so the fifth
        category onwards is invisible and the control looks like it has four
        options. Shortening the labels helped and did not fix it.

        So the row shows the FEW and a sheet holds the rest. The few are the
        four busiest kinds of work on this island — chosen deliberately rather
        than by usage, because a usage-ranked row reorders itself under people
        and nobody can learn where anything is. Whatever is selected is always
        in the row, so the current filter is never hidden inside the sheet.
      */}
      <div className="w-chip-row" role="group" aria-label={tw('w.jobs.all')}>
        <button
          className={category === '' ? 'w-chip on' : 'w-chip'}
          aria-pressed={category === ''}
          onClick={() => setCategory('')}
        >
          {tw('w.feed.allShort')}
        </button>
        {shown.map((c) => (
          <button
            key={c.id}
            className={category === c.id ? 'w-chip on' : 'w-chip'}
            aria-pressed={category === c.id}
            onClick={() => setCategory(c.id)}
          >
            {categoryShort(c)}
          </button>
        ))}
        {categories.length > shown.length && (
          <button className="w-chip w-chip-more" aria-expanded={sheet} onClick={() => setSheet(true)}>
            {tw('w.feed.more')} <Icon name="chevron" size={14} className="w-chip-caret" />
          </button>
        )}
      </div>

      {sheet && (
        <>
          {/* A backdrop, so a tap anywhere outside closes it — the gesture
              people already use for a sheet on a phone. */}
          <div className="w-scrim" onClick={() => setSheet(false)} aria-hidden="true" />
          <div className="w-sheet" role="dialog" aria-modal="true" aria-label={tw('w.profile.categories')}>
            <header>
              <h2>{tw('w.profile.categories')}</h2>
              <button className="link" onClick={() => setSheet(false)}>{tw('w.feed.done')}</button>
            </header>
            <div className="w-sheet-grid">
              <button
                className={category === '' ? 'w-sheet-item on' : 'w-sheet-item'}
                onClick={() => { setCategory(''); setSheet(false) }}
              >
                {tw('w.jobs.all')}
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  className={category === c.id ? 'w-sheet-item on' : 'w-sheet-item'}
                  onClick={() => { setCategory(c.id); setSheet(false) }}
                >
                  {/* The FULL name here: the sheet has the room, and this is
                      where somebody is reading rather than scanning. */}
                  {categoryName(c)}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/*
        Why there is no badge.

        A worker with no categories chosen can never be matched, and the old
        behaviour was to say nothing at all — so the feature looked broken
        rather than unconfigured. This says which it is, once, and only to
        somebody signed in who could act on it.
      */}
      {greetingName && !matchable && Boolean(jobs.length) && (
        <button type="button" className="w-nudge" onClick={onNeedProfile}>
          {tw('w.feed.noProfile')}
        </button>
      )}

      {/*
        How much arrived today.
        Counted from the postings already on screen rather than asked for
        separately — it is the same list, and a second endpoint could disagree
        with what the person is looking at.
      */}
      {greetingName && newToday > 0 && !q && !category && (
        <button className="w-today" onClick={() => setCategory('')}>
          <Icon name="megaphone" size={20} />
          <strong>{newToday} {newToday === 1 ? tw('w.feed.newTodayOne') : tw('w.feed.newToday')}</strong>
          <Icon name="chevron" size={18} className="w-today-go" />
        </button>
      )}

      {greetingName && Boolean(jobs.length) && (
        <header className="w-dash-head w-feed-head">
          <h2>{match && !q && !category ? tw('w.feed.recommended') : tw('w.feed.allJobs')}</h2>
        </header>
      )}

      {jobs.map((j) => (
        <Card
          key={j.id}
          job={j}
          onOpen={() => onOpen(j.id)}
          highlight={j.id === match?.id}
          matchReasons={j.id === match?.id ? match.match.reasons : undefined}
          saved={saved.has(j.id)}
          onToggleSave={greetingName ? () => void toggleSave(j.id) : undefined}
        />
      ))}

      {loading && <p className="w-state" role="status">{tw('w.jobs.loading')}</p>}
      {loadError && (
        <div className="w-state w-state-error" role="alert">
          <p>{tw('w.jobs.loadError')}</p>
          <button type="button" onClick={() => void load()}>{tw('w.jobs.retry')}</button>
        </div>
      )}
      {!loading && !loadError && jobs.length === 0 && (
        // Two different empty states: "nothing matches your search" is a
        // filter to clear, "nothing at all" is an app to come back to. One
        // message for both tells half the readers the wrong thing.
        <p className="muted w-empty">{q || category ? tw('w.jobs.none') : tw('w.jobs.noneAtAll')}</p>
      )}

      {next && (
        <button onClick={() => void load({ append: true, before: next })} disabled={loading}>
          {loading ? tw('w.jobs.loading') : tw('w.jobs.more')}
        </button>
      )}
    </div>
  )
}

/* ── One posting ──────────────────────────────────────────────────────────── */

export function Detail({
  id,
  signedIn,
  onBack,
  onApplied,
  onNeedProfile,
  profileRevision = 0,
  active = true,
}: {
  id: string
  signedIn: boolean
  onBack: () => void
  onApplied: () => void
  onNeedProfile?: () => void
  profileRevision?: number
  active?: boolean
}) {
  const [job, setJob] = useState<JobDetail | null>(null)
  const tr = useTranslation(job?.translation)
  const [saved, setSaved] = useState(false)
  const [applicationId, setApplicationId] = useState<string | null>(null)
  const [applying, setApplying] = useState(false)
  const [needsProfile, setNeedsProfile] = useState(false)
  const activeRef = useRef(active)
  const prerequisiteRequest = useRef(0)
  useEffect(() => { activeRef.current = active; if (!active) prerequisiteRequest.current++ }, [active])
  const [note, setNote] = useState('')
  const [availableFrom, setAvailableFrom] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Land on the title, not wherever the feed was scrolled to.
  useEffect(() => { if (active) window.scrollTo(0, 0) }, [id, active])

  useEffect(() => {
    void worx.job(id).then((r) => {
      setJob(r.job)
      setSaved(r.saved)
      setApplicationId(r.applicationId)
    }).catch(() => setError(tw('w.error.jobLoad')))
  }, [id])

  useEffect(() => {
    if (profileRevision > 0) { setNeedsProfile(false); setError(null) }
  }, [profileRevision])

  async function beginApply() {
    const request = ++prerequisiteRequest.current
    setApplying(true)
    setError(null)
    try {
      const { profile } = await worx.profile()
      if (request !== prerequisiteRequest.current || !activeRef.current) return
      if (!profile?.complete) {
        setNeedsProfile(true)
        onNeedProfile?.()
      } else setNeedsProfile(false)
    } catch {
      // The server still checks prerequisites if the read failed.
    }
  }

  async function toggleSave() {
    // Optimistic: the bookmark is the one control where a round trip before the
    // icon changes feels broken. A failure puts it back.
    const wanted = !saved
    setSaved(wanted)
    try {
      await worx.save(id, wanted)
    } catch {
      setSaved(!wanted)
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const r = await worx.apply(id, { note: note || undefined, availableFrom: availableFrom || undefined })
      setApplicationId(r.id)
      setApplying(false)
      celebrate({ title: tw('w.cheer.applied'), line: tw('w.cheer.appliedLine') })
      onApplied()
    } catch (err) {
      if ((err as { code?: string })?.code === 'profile_incomplete') {
        setNeedsProfile(true)
        setError(null)
        if (activeRef.current) onNeedProfile?.()
      } else setError(err instanceof Error ? err.message : tw('w.error.send'))
    } finally {
      setBusy(false)
    }
  }

  if (error && !job) return (
    <div className="w-state w-state-error" role="alert">
      <p>{error}</p>
      <button type="button" onClick={onBack}><Icon name="back" size={16} /> {tw('w.tab.jobs')}</button>
    </div>
  )
  if (!job) return <p className="w-state" role="status">{tw('w.jobs.loadingDetail')}</p>

  const open = job.canApply ?? job.status === 'published'

  return (
    <article className="w-detail">
      <div className="w-detail-main">
      <BackRow onBack={onBack}>
        <h1 className="w-detail-title">{tr.pick('title', job.title)}</h1>
      </BackRow>
      {tr.note}
      {job.isDemo && <DemoNotice practice={open} />}
      <p className="w-detail-employer">
        {job.employerName}{' '}
        <span className="w-verified"><Icon name="check" size={14} /> {tw(job.isDemo ? 'w.demo.business' : 'w.jobs.verified')}</span>
      </p>

      <p className="w-detail-pay">{payLabel(job)}</p>
      <ul className="w-facts">
        {job.location && <li>{job.location}</li>}
        {job.arrangement && <li>{tw(`w.arr.${job.arrangement}` as never)}</li>}
        {job.schedule && <li>{job.schedule}</li>}
        {job.startsOn && <li>{tw('w.jobs.starts')} {job.startsOn}</li>}
        {job.closesOn && <li>{tw('w.jobs.closes')} {job.closesOn}</li>}
      </ul>

      <section className="w-detail-section">
        <h2>{tw('w.jobs.description')}</h2>
        <p className="w-prose">{tr.pick('description', job.description)}</p>
      </section>

      {job.requirements && (
        <>
          <section className="w-detail-section">
            <h2>{tw('w.jobs.requirements')}</h2>
            <p className="w-prose">{tr.pick('requirements', job.requirements)}</p>
          </section>
        </>
      )}

      {(job.employerAboutEn ?? job.employerAboutEs) && (
        <>
          <section className="w-detail-section">
            <h2>{tw('w.jobs.about')}</h2>
            <p className="w-prose">{job.employerAboutEn ?? job.employerAboutEs}</p>
          </section>
        </>
      )}

      {!open && <p className="warn w-banner">{tw(`w.jobstatus.${job.status}` as WorxKey) ?? job.status}</p>}
      </div>

      {/* On a wide screen this is the card beside the posting; on a phone it follows it. */}
      <aside className="w-detail-side">
      {signedIn && open && (
        <h2 className="w-side-title">{tw('w.jobs.interested')}</h2>
      )}
      {signedIn && open && (
        <div className="w-actions">
          {applicationId ? (
            <button disabled className="w-applied"><Icon name="check" size={16} /> {tw('w.jobs.applied')}</button>
          ) : (
            <button className="primary" onClick={() => void beginApply()}>{tw(job.isDemo ? 'w.demo.apply' : 'w.jobs.apply')}</button>
          )}
          {/* One control, two states. The label says which state it is IN, and
              aria-pressed says the same thing to a screen reader — a bookmark
              whose only signal is a filled-vs-outline glyph is unreadable to
              half the people using it. */}
          <button className="w-save" onClick={() => void toggleSave()} aria-pressed={saved}>
            <Icon name="bookmark" size={16} className={saved ? 'w-icon-filled' : undefined} />
            {saved ? tw('w.jobs.unsave') : tw('w.jobs.save')}
          </button>
        </div>
      )}

      {/* Under the actions, not among them: it is available rather than
          suggested, and a posting is the thing on this app most likely to be
          worth reporting. */}
      {signedIn && <Report subjectType="job" subjectId={job.id} label={tw('w.report.job')} />}

      {applying && (
        <form className="w-apply" onSubmit={submit}>
          <h3>{tw('w.apply.title')}</h3>
          {needsProfile && <div className="w-guidance" role="alert">
            <p>{tw('w.profile.incomplete')}</p>
            {onNeedProfile && <button type="button" onClick={onNeedProfile}>{tw('w.guidance.completeProfile')}</button>}
          </div>}
          <label>
            {tw('w.apply.confirm')}
            <input type="date" value={availableFrom} onChange={(e) => setAvailableFrom(e.target.value)} />
          </label>
          <label>
            {tw('w.apply.note')}
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000} />
          </label>
          {error && <p className="error">{error}</p>}
          <button className="primary" type="submit" disabled={busy || needsProfile}>
            {busy ? '…' : tw('w.apply.send')}
          </button>
          <button type="button" disabled={busy} onClick={() => { setApplying(false); setNote(''); setAvailableFrom(''); setError(null) }}>{tw('w.guidance.cancelApplication')}</button>
        </form>
      )}
      </aside>
    </article>
  )
}

/* ── Saved ────────────────────────────────────────────────────────────────── */

export function Saved({ onOpen }: { onOpen: (id: string) => void }) {
  const [jobs, setJobs] = useState<JobCard[] | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [q, setQ] = useState('')
  const [category, setCategory] = useState('')

  useEffect(() => {
    void (async () => {
      const [s, c] = await Promise.all([
        worx.savedJobs().catch(() => ({ jobs: [] as JobCard[] })),
        worx.categories().catch(() => ({ categories: [] as Category[] })),
      ])
      setJobs(s.jobs)
      setCategories(c.categories)
    })()
  }, [])

  /*
   * Unsaving removes the row.
   *
   * This is the ONE list where that is right: everywhere else the bookmark is
   * a toggle and the job stays where it is, but a saved job you have just
   * unsaved is by definition not a saved job, and leaving it sitting there
   * with a hollow bookmark makes the reader wonder whether the tap worked.
   * The footer line says so before anybody has to find out.
   */
  const unsave = (id: string) => {
    setJobs((prev) => (prev ?? []).filter((j) => j.id !== id))
    void worx.save(id, false).catch(() => { void worx.savedJobs().then((r) => setJobs(r.jobs)) })
  }

  if (!jobs) return <p className="muted">…</p>
  if (!jobs.length) return <p className="muted w-empty">{tw('w.saved.none')}</p>

  /*
   * Only the categories actually saved get a chip.
   *
   * Fourteen chips over four jobs would be a filter row that is mostly dead
   * ends. The count on each one is the point: it says what is in there before
   * you tap it, which is what makes a filter worth having on a list this
   * short.
   */
  const counts = new Map<string, number>()
  for (const j of jobs) if (j.categoryId) counts.set(j.categoryId, (counts.get(j.categoryId) ?? 0) + 1)
  const chips = categories.filter((c) => counts.has(c.id))

  const needle = q.trim().toLowerCase()
  const shown = jobs.filter((j) => {
    if (category && j.categoryId !== category) return false
    if (!needle) return true
    return [j.title, j.employerName, j.location ?? ''].some((t) => t.toLowerCase().includes(needle))
  })

  return (
    <div className="w-feed">
      <h1 className="w-feed-heading">{tw('w.saved.title')}</h1>
      <p className="w-feed-sub">
        {jobs.length} {jobs.length === 1 ? tw('w.saved.one') : tw('w.saved.many')}
      </p>

      <div className="w-search">
        <Icon name="search" size={20} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={tw('w.saved.search')}
          aria-label={tw('w.saved.search')}
        />
      </div>

      <div className="w-fchips" role="group" aria-label={tw('w.profile.categories')}>
        <button
          className={category === '' ? 'w-fchip on' : 'w-fchip'}
          aria-pressed={category === ''}
          onClick={() => setCategory('')}
        >
          {tw('w.feed.allShort')} <span className="w-fchip-n">{jobs.length}</span>
        </button>
        {chips.map((c) => (
          <button
            key={c.id}
            className={category === c.id ? 'w-fchip on' : 'w-fchip'}
            aria-pressed={category === c.id}
            onClick={() => setCategory(c.id)}
          >
            {categoryShort(c)} <span className="w-fchip-n">{counts.get(c.id)}</span>
          </button>
        ))}
      </div>

      {shown.length === 0 && <p className="muted w-empty">{tw('w.saved.noMatch')}</p>}

      {shown.map((j) => (
        <SavedCard key={j.id} job={j} onOpen={() => onOpen(j.id)} onUnsave={() => unsave(j.id)} />
      ))}

      <p className="muted small w-list-hint">{tw('w.saved.hint')}</p>
    </div>
  )
}

/**
 * A saved job.
 *
 * Roomier than the feed's card on purpose. The feed is a list you scan past;
 * this is a list of things you already decided were worth coming back to, so
 * every fact you would want before tapping — pay, hours, where — is on the
 * face of it, and the business's own line sits in the corner where it reads as
 * a signature rather than as another field.
 */
function SavedCard({ job, onOpen, onUnsave }: { job: JobCard; onOpen: () => void; onUnsave: () => void }) {
  const soon = closesSoon(job.closesOn)
  const ended = job.status === 'filled' || job.status === 'closed'

  return (
    <article className={ended ? 'w-jcard w-jcard-ended' : 'w-jcard'}>
      <Logo name={job.employerName} src={job.employerLogoId} className="w-jcard-logo" />

      <div className="w-jcard-main">
        <div className="w-jcard-top">
          {/* Only when it is true, and only from the posting's own closing
              date — a badge that is always there stops being read. */}
          {soon && !ended && (
            <span className="w-soon"><Icon name="clock" size={14} /> {tw('w.jobs.closesSoon')}</span>
          )}
          {ended && <span className={`w-pill w-pill-${job.status}`}>{tw(`w.jobstatus.${job.status}` as WorxKey) ?? job.status}</span>}
          <span className="w-jcard-posted">{posted(job.publishedAt)}</span>
        </div>

        <h3 className="w-jcard-title">{job.title}</h3>
        <p className="w-jcard-emp">{job.employerName}</p>

        <ul className="w-cfacts">
          <li className="w-cfact">
            <span className="w-cfact-ic"><Icon name="money" size={16} /></span>
            {payShort(job)}
          </li>
          {job.arrangement && (
            <li className="w-cfact">
              <span className="w-cfact-ic"><Icon name="briefcase" size={16} /></span>
              {tw(`w.arr.${job.arrangement}` as never)}
            </li>
          )}
          {job.location && (
            <li className="w-cfact w-cfact-wide">
              <span className="w-cfact-ic"><Icon name="pin" size={16} /></span>
              {job.location}
            </li>
          )}
        </ul>
      </div>

      <button
        className="w-jcard-book"
        aria-pressed="true"
        aria-label={tw('w.jobs.unsave')}
        onClick={onUnsave}
      >
        <Icon name="bookmark" size={22} className="w-icon-filled" />
      </button>

      <div className="w-jcard-foot">
        <p className="w-jcard-tagline">{job.employerTagline}</p>
        <button className="w-cta" onClick={onOpen}>
          {tw('w.jobs.viewJob')} <Icon name="chevron" size={18} />
        </button>
      </div>
    </article>
  )
}

/**
 * Is this posting about to close?
 *
 * Seven days, and only when the date is in the future: a closing date that has
 * passed is not urgent, it is over, and the posting's own status says that.
 * `closesOn` is a plain date string, so it is compared as a date rather than
 * as an instant — a job closing "today" in Puerto Rico must not become
 * yesterday because the phone is in UTC.
 */
function closesSoon(closesOn: string | null): boolean {
  if (!closesOn) return false
  const today = new Date()
  const days = (Date.parse(`${closesOn}T23:59:59`) - today.getTime()) / 86_400_000
  return days >= 0 && days <= 7
}
